import { useHandler } from "@redotech/react-util/hook";
import {
  SegmentListMultipleItemMembershipOperator,
  SegmentPropertyMatchMode,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  rcoLineItemPropertiesSegmentSchema,
  RcoLineItemPropertiesSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import EditPencilIcon from "@redotech/redo-icons/edit-01.svg";
import { OperatorSelector } from "@redotech/redo-web-conversion/conversion/operator-selector";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { PartialWithRequiredType } from "../..";
import { PropertySelectionModal } from "./property-selection-modal";

export const LineItemPropertiesInspectorContent = memo(
  function LineItemPropertiesInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoLineItemPropertiesSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(
              segment.cartOperator,
              segment.propertiesListOperator,
            )}
          </Text>
        </Flex>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.properties.length === 0 ? (
            <Text fontSize="xs" textColor="secondary">
              {t("No properties selected")}
            </Text>
          ) : (
            segment.properties.map((property, index) => (
              <RedoBadge
                color="gray"
                key={index}
                size="sm"
                text={formatPropertyBadgeText(property)}
              />
            ))
          )}
        </Flex>
      </>
    );
  },
);

export const EditableLineItemPropertiesInspectorContent = memo(
  function EditableLineItemPropertiesInspectorContent({
    step,
    setStep,
  }: {
    step: PartialWithRequiredType<RcoSegmentStep>;
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const [isPropertySelectionModalOpen, setIsPropertySelectionModalOpen] =
      useState(false);

    const segment = useMemo(
      () => rcoLineItemPropertiesSegmentSchema.parse(step.segment),
      [step],
    );

    const handleCartOperatorChange = useHandler(
      (cartOperator: SegmentListMultipleItemMembershipOperator) => {
        const updatedSegment: RcoLineItemPropertiesSegmentSchema = {
          ...segment,
          cartOperator,
        };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handlePropertiesListOperatorChange = useHandler(
      (propertiesListOperator: SegmentListMultipleItemMembershipOperator) => {
        const updatedSegment: RcoLineItemPropertiesSegmentSchema = {
          ...segment,
          propertiesListOperator,
        };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handlePropertiesChange = useHandler(
      (
        properties: {
          key: string;
          value: string;
          matchMode?: SegmentPropertyMatchMode;
        }[],
      ) => {
        const updatedSegment: RcoLineItemPropertiesSegmentSchema = {
          ...segment,
          properties,
        };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleOpenPropertySelectionModal = useHandler(() => {
      setIsPropertySelectionModalOpen(true);
    });

    const handleClosePropertySelectionModal = useHandler(() => {
      setIsPropertySelectionModalOpen(false);
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Flex>
            <OperatorSelector<SegmentListMultipleItemMembershipOperator>
              availableOptions={Object.values(
                SegmentListMultipleItemMembershipOperator,
              )}
              onOperatorSelected={handleCartOperatorChange}
              operatorToText={segmentListMultipleItemMembershipOperatorToText()}
              selectedOperator={segment.cartOperator}
            />
          </Flex>
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("item in the cart")}
        </Text>
        <Flex>
          <OperatorSelector<SegmentListMultipleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListMultipleItemMembershipOperator,
            )}
            onOperatorSelected={handlePropertiesListOperatorChange}
            operatorToText={propertiesListMultipleItemMembershipOperatorToText()}
            selectedOperator={segment.propertiesListOperator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("the properties in this list:")}
        </Text>
        <Flex dir="row" gap="md" w="full" wrap="wrap">
          {segment.properties.length === 0 ? (
            <Text fontSize="xs" textColor="secondary">
              {t("No properties selected")}
            </Text>
          ) : (
            segment.properties.map((property, index) => (
              <RedoBadge
                color="gray"
                key={index}
                size="sm"
                text={formatPropertyBadgeText(property)}
              />
            ))
          )}
        </Flex>
        <Flex justify="flex-end" w="full">
          <RedoButton
            hierarchy="secondary"
            IconLeading={EditPencilIcon}
            onClick={handleOpenPropertySelectionModal}
            size="xs"
            text={t("Edit list")}
          />
        </Flex>

        {isPropertySelectionModalOpen && (
          <PropertySelectionModal
            onClose={handleClosePropertySelectionModal}
            onSave={handlePropertiesChange}
            selectedProperties={segment.properties}
          />
        )}
      </>
    );
  },
);

function formatPropertyBadgeText(property: {
  key: string;
  value: string;
  matchMode?: SegmentPropertyMatchMode;
}): string {
  const mode = property.matchMode ?? "exact";
  switch (mode) {
    case "exact":
      return `${property.key}=${property.value}`;
    case "wildcard":
      return `${property.key} ~ ${property.value}`;
    default:
      assertNever(mode);
  }
}

function segmentListMultipleItemMembershipOperatorToText(): Record<
  SegmentListMultipleItemMembershipOperator,
  string
> {
  return {
    [SegmentListMultipleItemMembershipOperator.ANY_IN]: t("Any"),
    [SegmentListMultipleItemMembershipOperator.NONE_IN]: t("No"),
    [SegmentListMultipleItemMembershipOperator.ALL_IN]: t("Every"),
  };
}

function propertiesListMultipleItemMembershipOperatorToText(): Record<
  SegmentListMultipleItemMembershipOperator,
  string
> {
  return {
    [SegmentListMultipleItemMembershipOperator.ANY_IN]: t("any of"),
    [SegmentListMultipleItemMembershipOperator.ALL_IN]: t("all of"),
    [SegmentListMultipleItemMembershipOperator.NONE_IN]: t("none of"),
  };
}

function operatorsToText(
  cartOperator: SegmentListMultipleItemMembershipOperator,
  propertiesListOperator: SegmentListMultipleItemMembershipOperator,
): string {
  switch (cartOperator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      switch (propertiesListOperator) {
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return t(
            "at least one item in the cart has any of the configured properties",
          );
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return t(
            "at least one item in the cart has all of the configured properties",
          );
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return t(
            "at least one item in the cart has none of the configured properties",
          );
        default:
          return assertNever(propertiesListOperator);
      }
    case SegmentListMultipleItemMembershipOperator.ALL_IN:
      switch (propertiesListOperator) {
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return t(
            "all items in the cart have any of the configured properties",
          );
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return t(
            "all items in the cart have all of the configured properties",
          );
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return t(
            "all items in the cart have none of the configured properties",
          );
        default:
          return assertNever(propertiesListOperator);
      }
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      switch (propertiesListOperator) {
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return t(
            "no items in the cart have any of the configured properties",
          );
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return t(
            "no items in the cart have all of the configured properties",
          );
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return t(
            "no items in the cart have none of the configured properties",
          );
        default:
          return assertNever(propertiesListOperator);
      }
    default:
      return assertNever(cartOperator);
  }
}
